<script setup lang="ts">
import { ref, computed, watch } from 'vue';

type FilterEvent = { search?: string; value?: string };

const props = withDefaults(defineProps<{
    modelValue: string[],
    placeHolder?: string,
    options?: string[]
}>(), {
    modelValue: () => [],
    placeHolder: '',
    options: () => []
});

const emit = defineEmits(['update:modelValue']);

const modelValueProxy = computed<string[]>({
    get: () => props.modelValue,
    set: (val: string[]) => emit('update:modelValue', val)
});


const labelSearchTerm = ref('');
const labels = computed(() => props.options ?? []);
const labelOptions = ref<string[]>([...modelValueProxy.value]);

watch(
    () => [labels.value, modelValueProxy.value],
    () => {
        sortAndUpdateLabelOptions();
    },
    { immediate: true, deep: true }
);

function sortAndUpdateLabelOptions () {
    const selected = sortByProperty([...modelValueProxy.value], '', true);
    const all = labels.value;
    const rest = sortByProperty(all.filter((l: string) => !selected.includes(l)), '', true) as string[];
    labelOptions.value = [...selected, ...rest];
}

function addSearchTermToLabelOptions () {
    const proxyVals = Array.isArray(modelValueProxy.value) ? modelValueProxy.value : [];
    const currentOptions = [...proxyVals];

    if (labelSearchTerm.value && !currentOptions.includes(labelSearchTerm.value)) {
        currentOptions.push(labelSearchTerm.value);
    }
    labelOptions.value = Array.from(
        new Set([...(labels.value || []), ...currentOptions])
    );
}

function handleFilter (event: FilterEvent) {
    const search = event.search ?? event.value ?? '';
    labelSearchTerm.value = search;
    addSearchTermToLabelOptions();
}

</script>

<template>
    <VLMultiSelect
        v-model="modelValueProxy"
        display="chip"
        :options="labelOptions"
        :filter="true"
        :placeholder="props.placeHolder"
        class="w-full"
        :allow-create="true"
        @filter="handleFilter"
        @show="sortAndUpdateLabelOptions"
    />
</template>

<style scoped>

:deep(.p-multiselect-label.p-placeholder) {
    padding-top: 24px;
}

:deep(.p-multiselect-label) {
    flex-wrap: wrap;
    padding-top: 16px;
}

:deep(span.p-multiselect-chip-item) {
    margin-top: 4px;
}
</style>
